import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import * as pt_br from 'dayjs/locale/pt-br';

dayjs.locale(pt_br);
dayjs.extend(utc);
dayjs.extend(timezone);

import {
  IPeriod,
  PeriodType,
  prePeriodGeneric,
} from '../period-select/interface';

export const formatPeriod = (period: prePeriodGeneric | undefined): IPeriod => {
  const typeGenericOPtions = {
    months: 'MONTHS',
    custom: 'CUSTOM',
    undefined: 'otimo',
    default: 'otimo',
    overlap_period: 'otimo',
    '1': '1M',
    '3': '3M',
    '6': '6M',
    '12': '1A',
    '24': '2A',
    '36': '3A',
    '48': '5A',
  };

  if (!period)
    return {
      type: 'otimo',
    };

  const type =
    period?.type &&
    typeGenericOPtions?.[period?.type as keyof typeof typeGenericOPtions];
  const value = period?.value ? (period.value as string) : undefined;
  const startDate = period?.startDate ? period.startDate : '';
  const endDate = period?.endDate ? period.endDate : '';

  return {
    type: type ? (type as PeriodType) : 'otimo',
    value,
    startDate: startDate!,
    endDate: endDate!,
  };
};

export const getDateWithoutTimezone = (
  date: number | string,
  {
    type = 'epoch',
    formatDate = 'DD-MM-YYYY',
    defaultDate,
  }: IGetDateWithoutTimezone = {}
) => {
  if (!date) return defaultDate;

  const dayjsDate = dayjs(date)
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .add(dayjs(date).utcOffset(), 'minute');

  switch (type) {
    case 'epoch':
      return dayjsDate.valueOf();
    case 'date':
      return dayjsDate.toDate();
    case 'format':
      return dayjsDate.format(formatDate);
    default:
      return dayjsDate.toDate();
  }
};

export const fixMinMaxForDatePicker = (date: number | string | null) => {
  if (!date) return null;

  const dayjsDate = dayjs(date).add(dayjs(date).utcOffset() * -1, 'minute');

  return dayjsDate.valueOf();
};

export const formatDate = (
  date?: Date | string | number | null,
  format: string = 'DD/MM/YYYY'
) => {
  if (!date) return '-';
  if (!dayjs(date).isValid()) return '-';

  return dayjs(date).format(format);
};

export const formatDateWithTime = (
  date?: Date | string | number,
  format: string = 'DD/MM/YYYY HH:mm'
) => {
  if (!date) return '-';
  if (!dayjs(date).isValid()) return '-';

  return dayjs(date).format(format);
};

export const isDate = (date: string) => {
  if (date?.length < 5) return false;

  return dayjs(date).isValid();
};

export const getUtcDate = (
  date: string | number | undefined | null,
  formatDate = 'DD-MM-YYYY',
  showLocalTime = false // Show UTC time in local time
): string => {
  if (!date) return '-';

  if (showLocalTime) {
    return dayjs.utc(date).local().format(formatDate);
  }

  return dayjs(date).utc().format(formatDate);
};

interface IGetDateWithoutTimezone {
  type?: 'epoch' | 'date' | 'format';
  formatDate?: string;
  defaultDate?: string | number;
}

export const getRelativeEpoch = (
  periodToSubtract: number,
  lastDateEpoch?: number
): number => {
  return dayjs(lastDateEpoch)
    .subtract(periodToSubtract, 'month')
    .add(1, 'day')
    .valueOf();
};

export { dayjs };
